import React, { useState, useEffect } from 'react';
import {
  Grid,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Button,
  Alert,
  FormHelperText,
  Divider,
  Paper
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

const RetryStrategyForm = ({
  strategy,
  onSave,
  onCancel,
  mode = 'create' // 'create' | 'edit' | 'duplicate'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    max_attempts: 3,
    base_delay_minutes: 30,
    backoff_factor: 2.0,
    max_delay_minutes: 1440,
    retry_on_failure: true,
    retry_on_timeout: true,
    retry_on_error: true,
    notify_on_max_retries: false,
    notify_recipients: '',
    is_active: true
  });

  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Initialize form data when strategy prop changes
  useEffect(() => {
    if (strategy) {
      setFormData({
        name: mode === 'duplicate' ? `${strategy.name} (Copy)` : strategy.name,
        description: strategy.description || '',
        max_attempts: strategy.max_attempts || 3,
        base_delay_minutes: strategy.base_delay_minutes || 30,
        backoff_factor: strategy.backoff_factor || 2.0,
        max_delay_minutes: strategy.max_delay_minutes || 1440,
        retry_on_failure: strategy.retry_on_failure ?? true,
        retry_on_timeout: strategy.retry_on_timeout ?? true,
        retry_on_error: strategy.retry_on_error ?? true,
        notify_on_max_retries: strategy.notify_on_max_retries ?? false,
        notify_recipients: strategy.notify_recipients || '',
        is_active: strategy.is_active ?? true
      });
    }
  }, [strategy, mode]);

  const validateForm = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Strategy name is required';
    }

    // Numeric validations
    if (formData.max_attempts < 1) {
      newErrors.max_attempts = 'Max attempts must be at least 1';
    }

    if (formData.base_delay_minutes < 1) {
      newErrors.base_delay_minutes = 'Base delay must be at least 1 minute';
    }

    if (formData.backoff_factor <= 0) {
      newErrors.backoff_factor = 'Backoff factor must be greater than 0';
    }

    if (formData.max_delay_minutes < formData.base_delay_minutes) {
      newErrors.max_delay_minutes = 'Max delay must be at least the base delay';
    }

    // Email validation for notify recipients
    if (formData.notify_on_max_retries && formData.notify_recipients) {
      const emails = formData.notify_recipients.split(',').map(email => email.trim());
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const invalidEmails = emails.filter(email => email && !emailRegex.test(email));
      if (invalidEmails.length > 0) {
        newErrors.notify_recipients = 'Invalid email format(s)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await onSave(formData);
    } catch (error) {
      console.error('Error saving retry strategy:', error);
    } finally {
      setSaving(false);
    }
  };

  const calculateRetryDelays = () => {
    const delays = [];
    let currentDelay = formData.base_delay_minutes;
    
    for (let attempt = 1; attempt <= formData.max_attempts; attempt++) {
      delays.push(Math.min(currentDelay, formData.max_delay_minutes));
      currentDelay *= formData.backoff_factor;
    }
    
    return delays;
  };

  const renderRetryPreview = () => {
    const delays = calculateRetryDelays();
    
    return (
      <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" gutterBottom>
          Retry Behavior Preview
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {delays.map((delay, index) => (
            <Box
              key={index}
              sx={{
                p: 1,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: 1,
                borderColor: 'divider',
                minWidth: 80,
                textAlign: 'center'
              }}
            >
              <Typography variant="caption" display="block" color="text.secondary">
                Attempt {index + 1}
              </Typography>
              <Typography variant="body2" fontWeight="bold" color="text.primary">
                {delay} min
              </Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Total time: {delays.reduce((sum, delay) => sum + delay, 0)} minutes
        </Typography>
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Basic Information
          </Typography>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Strategy Name"
            value={formData.name}
            onChange={handleInputChange('name')}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_active}
                onChange={handleInputChange('is_active')}
              />
            }
            label="Active"
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={handleInputChange('description')}
            multiline
            rows={3}
            helperText="Optional description of this retry strategy"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Retry Configuration
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max Attempts"
            type="number"
            value={formData.max_attempts}
            onChange={handleInputChange('max_attempts')}
            inputProps={{ min: 1, max: 10 }}
            error={!!errors.max_attempts}
            helperText={errors.max_attempts || "Maximum number of retry attempts"}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Base Delay (minutes)"
            type="number"
            value={formData.base_delay_minutes}
            onChange={handleInputChange('base_delay_minutes')}
            inputProps={{ min: 1 }}
            error={!!errors.base_delay_minutes}
            helperText={errors.base_delay_minutes || "Initial delay before first retry"}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Backoff Factor"
            type="number"
            value={formData.backoff_factor}
            onChange={handleInputChange('backoff_factor')}
            inputProps={{ min: 0.1, step: 0.1 }}
            error={!!errors.backoff_factor}
            helperText={errors.backoff_factor || "Multiplier for exponential backoff (e.g., 2.0 = double each time)"}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Max Delay (minutes)"
            type="number"
            value={formData.max_delay_minutes}
            onChange={handleInputChange('max_delay_minutes')}
            inputProps={{ min: formData.base_delay_minutes }}
            error={!!errors.max_delay_minutes}
            helperText={errors.max_delay_minutes || "Maximum delay cap"}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Retry Conditions
          </Typography>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.retry_on_failure}
                onChange={handleInputChange('retry_on_failure')}
              />
            }
            label="Retry on General Failures"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.retry_on_timeout}
                onChange={handleInputChange('retry_on_timeout')}
              />
            }
            label="Retry on Timeouts"
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.retry_on_error}
                onChange={handleInputChange('retry_on_error')}
              />
            }
            label="Retry on Errors"
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Notifications
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.notify_on_max_retries}
                onChange={handleInputChange('notify_on_max_retries')}
              />
            }
            label="Notify on Max Retries Reached"
          />
        </Grid>

        {formData.notify_on_max_retries && (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Notification Recipients"
              value={formData.notify_recipients}
              onChange={handleInputChange('notify_recipients')}
              error={!!errors.notify_recipients}
              helperText={errors.notify_recipients || "Comma-separated email addresses"}
              placeholder="admin@example.com, support@example.com"
            />
          </Grid>
        )}

        {/* Retry Preview */}
        <Grid item xs={12}>
          {renderRetryPreview()}
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              onClick={onCancel}
              variant="outlined"
              startIcon={<CancelIcon />}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={saving}
            >
              {saving ? 'Saving...' : mode === 'create' ? 'Create Strategy' : 'Save Changes'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RetryStrategyForm; 